import { useNavigate } from 'react-router-dom'
import { Rocket } from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { SectionHelp } from '@/components/help/section-help'
import { EmptyState } from '@/components/empty-state'
import { PlanGate } from '@/components/plan-gate'
import { usePlan } from '@/hooks/use-plan'
import { StatsCards } from '@/features/dashboard/stats-cards'
import { PipelineOverview } from '@/features/dashboard/pipeline-overview'
import { OutreachFunnel } from '@/features/dashboard/outreach-funnel'
import { StrategyPerformance } from '@/features/dashboard/strategy-performance'
import { ActivityChart } from '@/features/dashboard/activity-chart'
import { TopMessages } from '@/features/dashboard/top-messages'
import { QuickActions } from '@/features/dashboard/quick-actions'
import { ActivityFeed } from '@/features/dashboard/activity-feed'
import { ComingSoonCard } from '@/features/dashboard/coming-soon'
import { DashboardFilterBar } from '@/features/dashboard/dashboard-filters'
import { useDashboardStats, useIsEmptyDashboard } from '@/features/dashboard/use-dashboard'

export default function DashboardPage() {
  // Unfiltered on purpose: a filter that matches nothing must not bring
  // back the first-run welcome screen.
  const { data: stats } = useDashboardStats('')
  const isEmpty = useIsEmptyDashboard(stats)
  const navigate = useNavigate()
  const plan = usePlan()

  if (isEmpty) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title="Dashboard"
          subtitle="Welcome back. Here's what's happening."
          actions={<SectionHelp sectionKey="dashboard" />}
        />
        <EmptyState
          icon={Rocket}
          title="Welcome to DealCloser Autopilot"
          description="Get started by creating a campaign, then adding leads to it. Your dashboard will come alive once your automation is running."
          actionLabel="Create Campaign"
          onAction={() => navigate('/campaigns')}
        />
        <QuickActions />
        <ComingSoonCard />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Dashboard"
        subtitle="Welcome back. Here's what's happening."
        actions={<SectionHelp sectionKey="dashboard" />}
      />
      <DashboardFilterBar />
      <div data-tour="dashboard-stats">
        <StatsCards />
      </div>
      <div data-tour="dashboard-funnel">
        <OutreachFunnel />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PlanGate requiredPlan="pro" currentPlan={plan}>
          <StrategyPerformance />
        </PlanGate>
        <PlanGate requiredPlan="pro" currentPlan={plan}>
          <ActivityChart />
        </PlanGate>
      </div>
      <PlanGate requiredPlan="pro" currentPlan={plan}>
        <TopMessages />
      </PlanGate>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <PipelineOverview />
        </div>
        <div data-tour="dashboard-quick-actions">
          <QuickActions />
        </div>
      </div>
      <div data-tour="dashboard-activity">
        <ActivityFeed />
      </div>
      <ComingSoonCard />
    </div>
  )
}
